import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import { journalAPI } from '../services/api';
import XPToast from '../components/ui/XPToast';
import GrammarErrorPopup from '../components/ui/GrammarErrorPopup';
import SpeakButton from '../components/ui/SpeakButton';
import { PencilIcon, ClockIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const WritingJournalPage = () => {
  const [text, setText]           = useState('');
  const [entries, setEntries]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]       = useState(null); // last check result
  const [xpToast, setXpToast]     = useState(null);
  const [selectedError, setSelectedError] = useState(null);

  const loadEntries = useCallback(async () => {
    try {
      const res = await journalAPI.list();
      setEntries(res.data.entries || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await journalAPI.submit(text);
      const data = res.data;
      setResult(data);
      if (data.xp_awarded > 0) setXpToast({ amount: data.xp_awarded, reason: 'journal_entry' });
      setText('');
      // Prepend to local list
      setEntries(prev => [data, ...prev]);
    } catch (e) {
      console.error(e);
    }
    setSubmitting(false);
  };

  const scoreColor = (score) => {
    if (score >= 90) return 'text-emerald-600 bg-emerald-50';
    if (score >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <DashboardLayout>
      <div className="page-shell-wide space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <PencilIcon className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">Ditari im në Gjermanisht</h1>
            <p className="text-sm text-zinc-500">Shkruaj fjali dhe merr korigjim gramatike</p>
          </div>
        </div>

        {/* Input area */}
        <div className="rounded-2xl bg-white shadow-md overflow-hidden">
          <textarea
            id="journal-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Shkruaj fjali në gjermanisht... p.sh. Ich lerne heute Deutsch."
            rows={4}
            className="w-full p-4 text-sm text-zinc-800 placeholder:text-zinc-400 resize-none focus:outline-none"
          />
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100 bg-zinc-50/50">
            <span className="text-xs text-zinc-400">{text.length} karaktere</span>
            <button
              onClick={handleSubmit}
              disabled={!text.trim() || submitting}
              className="px-5 py-2 bg-zinc-900 text-white text-sm font-medium rounded-xl hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              {submitting ? 'Duke kontrolluar...' : '✓ Kontrollo Gramatikën'}
            </button>
          </div>
        </div>

        {/* Last result */}
        {result && (
          <div className="rounded-2xl bg-white shadow-md p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-zinc-900">Rezultati</h3>
              <div className="flex items-center gap-2">
                <SpeakButton text={result.text} lang="de-DE" />
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${scoreColor(result.score)}`}>
                  {result.score}%
                </span>
              </div>
            </div>

            <p className="text-sm text-zinc-700 font-medium italic">"{result.text}"</p>

            {result.errors && result.errors.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Gabimet e Gjetura</p>
                {result.errors.map((err, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedError(err)}
                    className="w-full text-left flex items-start gap-2.5 p-3 rounded-xl bg-red-50 border border-red-200 hover:bg-red-100 transition-colors cursor-pointer"
                  >
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-red-700">{err.error_type}</p>
                      <p className="text-xs text-red-600 mt-0.5">{err.explanation_sq}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircleIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Asnjë gabim! Shkëlqyeshëm!</span>
              </div>
            )}

            {result.xp_awarded > 0 && (
              <p className="text-xs font-semibold text-amber-700">+{result.xp_awarded} XP fituar</p>
            )}
          </div>
        )}

        {/* Past entries */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider">Hyrjet e Mëparshme</h2>
          {loading && <div className="animate-pulse h-16 rounded-xl bg-zinc-100" />}
          {!loading && entries.length === 0 && (
            <p className="text-sm text-zinc-400 italic text-center py-8">
              Nuk ka hyrje akoma. Shruaj diçka sipër!
            </p>
          )}
          {entries.map((e) => (
            <div key={e.id} className="rounded-xl bg-white p-4 shadow-sm space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                  <ClockIcon className="w-3.5 h-3.5" />
                  {new Date(e.created_at).toLocaleDateString('sq-AL', {
                    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <SpeakButton text={e.text} lang="de-DE" />
                  {e.score != null && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${scoreColor(e.score)}`}>
                      {e.score}%
                    </span>
                  )}
                  {e.xp_awarded > 0 && (
                    <span className="text-xs font-semibold text-amber-700">+{e.xp_awarded} XP</span>
                  )}
                </div>
              </div>
              <p className="text-sm text-zinc-700">{e.text}</p>
              {e.errors?.length > 0 && (
                <p className="text-xs text-red-500">{e.errors.length} gabim{e.errors.length > 1 ? 'e' : ''} i gjetur</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {selectedError && (
        <GrammarErrorPopup error={selectedError} onClose={() => setSelectedError(null)} />
      )}

      {xpToast && (
        <XPToast amount={xpToast.amount} reason={xpToast.reason} onDone={() => setXpToast(null)} />
      )}
    </DashboardLayout>
  );
};

export default WritingJournalPage;
